import * as React from 'react'
import { Pause, Play, Radar, X } from 'lucide-react'
import type { HuntLiveDto, HuntPoll } from '@/hooks/use-automation'
import { useHuntTrace, useToggleHuntTrace } from '@/hooks/use-automation'
import { cn } from '@/lib/cn'
import { buildDigest, count, duration, stamp } from './hunt-digest'
import { HuntStages, stageIndex } from './hunt-stages'
import {
  AccountsTab,
  ActiveHitsTab,
  ClaimTab,
  ErrorsTab,
  OverviewTab,
  RequestsTab,
  roleTag,
  SearchTab,
  GaTab,
  traceLine,
  useCopiedFlag,
  WatchWorkersProvider,
  WorkerLogTab,
  WorkersTab,
} from './hunt-tabs'

/**
 * The live worker view for one hunt.
 *
 * ── Why this is not in the run log ──────────────────────────────────
 * The log is a capped ring of ~60 lines in Mongo, shared with the entire
 * sign-in story. A hunt polls tens of times a second, so one line per check
 * would erase everything else within seconds and cost a database write per
 * poll. The log carries the milestones; this carries the evidence.
 *
 * ── Six questions, four tabs ────────────────────────────────────────
 * The structure is derived from what someone actually needs to know, in the
 * order they need it, rather than from a visual metaphor:
 *
 *   Overview  should I care, and where do I go?
 *   Workers   is each one alive, throttled, still rotating?
 *   Requests  who called what, and what came back?
 *   Errors    who refused me, and what did they say?
 *
 * Panels are hidden rather than unmounted, so scroll position, an expanded
 * response body and the `all 60` toggle all survive a tab switch.
 *
 * ── Why it stays cheap ──────────────────────────────────────────────
 * It repaints ~1.4 times a second for as long as a race lasts — hours — so
 * every per-render cost is paid tens of thousands of times. Four things keep it
 * flat, and all four are load-bearing:
 *
 *   1. ONE derivation pass (`buildDigest`) memoised on the response, feeding
 *      every tab. Not one pass per tab.
 *   2. A hidden tab's props are HELD STILL, so its `React.memo` bails on a
 *      single `Object.is` instead of re-rendering to paint pixels nobody can
 *      see. This is what makes four tabs cost about the same as one.
 *   3. `buildDigest` only collects references out of the ring, so React Query's
 *      structural sharing survives and every unchanged row memo-bails too.
 *   4. Rows are `content-visibility: auto`, so scrolled-out rows cost no layout
 *      or paint at all.
 */

/* ------------------------------------------------------------------ *
 * Tabs
 * ------------------------------------------------------------------ */

type TabKey =
  | 'overview'
  | 'claim'
  | 'active'
  | 'workers'
  | 'log'
  | 'search'
  | 'ga'
  | 'accounts'
  | 'requests'
  | 'errors'

/**
 * How many checks the Requests list shows before asking.
 *
 * The server keeps sixty. The twenty newest are the ones anyone is reading; the
 * rest are one click away. A finished hunt defaults to all of them, because
 * nothing is arriving to push them off.
 */
const WINDOW = 20

export const HuntPanel = React.memo(function HuntPanel({
  hunt,
  worker,
  onSelectWorker,
}: {
  hunt: HuntLiveDto
  /*
   * The selected worker is OWNED by the drawer, not by this panel.
   *
   * It is a request parameter: the worker's call log and its kept failures are
   * fetched with `?worker=`, and the component that owns the query has to know
   * which one. The panel still drives every change; it just no longer holds the
   * value, which is what stops the selection and the fetch drifting apart.
   */
  worker: number | null
  onSelectWorker: (worker: number | null) => void
}) {
  const [tab, setTab] = React.useState<TabKey>('overview')
  const focus = worker
  const setFocus = onSelectWorker
  const [all, setAll] = React.useState(false)
  const [frozen, setFrozen] = React.useState(false)

  /*
   * Freeze, because a list that rewrites itself 1.4 times a second cannot be
   * read.
   *
   * The moment anyone wants this panel is the moment something looks wrong, and
   * that is exactly when the row they are squinting at scrolls away. Freezing
   * holds the last snapshot on screen while the hunt carries on underneath —
   * nothing is paused on the server, and unfreezing catches up instantly.
   */
  const held = React.useRef(hunt)
  if (!frozen) held.current = hunt
  const view = frozen ? held.current : hunt

  /* One pass. Every number on every tab comes out of this. */
  const digest = React.useMemo(() => buildDigest(view), [view])

  /*
   * Focus is cross-tab on purpose.
   *
   * "w3 looks wrong" is one thought, and it should not have to be re-expressed
   * once per tab. Clicking a worker anywhere filters the request stream and
   * highlights that worker's row everywhere; clicking it again clears it.
   */
  /*
   * Clicking a worker anywhere focuses it AND opens its log.
   *
   * "w3 looks wrong" is one thought and it should take one click. Clicking the
   * same worker again clears the filter but leaves the tab, so the second click
   * widens the view rather than throwing away where you were.
   */
  const onFocus = React.useCallback(
    (w: number) => {
      setFocus(w === focus ? null : w)
      if (w === focus) return
      /*
       * No jump at all while already on Search, which is itself a per-worker view.
       *
       * There the worker picker IS the tab's own control, so navigating away
       * would make choosing a worker the one action that closes the panel you
       * chose it in.
       */
      if (tab === 'search' || tab === 'ga') return
      /*
       * Which fleet the worker belongs to decides WHERE "show me this one"
       * goes, because the fleets keep their evidence in different places.
       *
       * For a schedule watcher the Log is the whole story: a status and a timing
       * per call, and a 200 there means the shift's status was read. For a
       * shift-list worker it is not — that fleet's failure mode is a 200 carrying
       * an empty list, invisible in a status code, so it has a tab carrying the
       * worker's request headers and response bodies. Sending everyone to the
       * Log made the second click the one that mattered, and only if you already
       * knew to make it.
       *
       * The GA fleet is the other, and the one it matters most for: its failure
       * is a 200 carrying no total at all, and the Log would show a healthy 200
       * in 180ms beside it. A row still stamped with the retired job role falls
       * to the Log, which is where its calls are.
       */
      const role = view.workers.find((x) => x?.worker === w)?.role
      setTab(role === 'shift-list' ? 'search' : role === 'ga' ? 'ga' : 'log')
    },
    [focus, setFocus, tab, view.workers]
  )
  const clearFocus = React.useCallback(() => setFocus(null), [setFocus])
  const openErrors = React.useCallback(() => setTab('errors'), [])
  const toggleAll = React.useCallback(() => setAll((a) => !a), [])

  /* ---- the full trace: opt-in, fetched on demand, never on the poll ---- */
  const traceOn = view.trace?.on === true
  const [traceLoaded, setTraceLoaded] = React.useState(false)
  const [copied, markCopied] = useCopiedFlag()
  const traceQuery = useHuntTrace(hunt.customerId, traceLoaded)
  const toggleTrace = useToggleHuntTrace(hunt.customerId)

  const onToggleTrace = React.useCallback(() => {
    const next = !traceOn
    toggleTrace.mutate(next)
    // Turning recording OFF also drops whatever is on screen: the records are
    // gone server-side, so leaving them rendered would be showing a snapshot of
    // something that no longer exists with a live-looking control above it.
    if (!next) setTraceLoaded(false)
  }, [traceOn, toggleTrace])

  const onLoadTrace = React.useCallback(() => {
    setTraceLoaded(true)
    void traceQuery.refetch()
  }, [traceQuery])

  const onClearTrace = React.useCallback(() => setTraceLoaded(false), [])

  const onCopyTrace = React.useCallback(
    (rows: HuntPoll[]) => {
      // Oldest first in the clipboard, whatever the screen shows — a log that is
      // going to be read outside the browser reads forwards in time.
      const text = [...rows].reverse().map(traceLine).join('\n')
      void navigator.clipboard.writeText(text).then(markCopied, () => {
        /* a denied clipboard is not worth an error state */
      })
    },
    [markCopied]
  )

  /*
   * One stable object, because `RequestsTab` is memoised.
   *
   * Ten separate props, several of them freshly-allocated callbacks, would
   * change identity on every poll and defeat the hidden-tab optimisation this
   * whole panel is built around — the Requests tab would re-render 1.4 times a
   * second while nobody was looking at it.
   */
  const traceControls = React.useMemo(
    () => ({
      on: traceOn,
      size: view.trace?.size ?? 0,
      records: traceLoaded ? (traceQuery.data?.records ?? null) : null,
      busy: toggleTrace.isPending,
      loading: traceQuery.isFetching,
      copied,
      onToggle: onToggleTrace,
      onLoad: onLoadTrace,
      onClear: onClearTrace,
      onCopy: onCopyTrace,
    }),
    [
      traceOn,
      view.trace?.size,
      traceLoaded,
      traceQuery.data,
      traceQuery.isFetching,
      toggleTrace.isPending,
      copied,
      onToggleTrace,
      onLoadTrace,
      onClearTrace,
      onCopyTrace,
    ]
  )

  /*
   * Filters live OUTSIDE the digest memo.
   *
   * Changing the focused worker must not re-walk the ring — it is a filter over
   * an array of at most sixty references, which is cheaper than the bookkeeping
   * a memo would need.
   */
  const rows = React.useMemo(() => {
    const list = focus === null ? digest.newestFirst : digest.newestFirst.filter((p) => p.worker === focus)
    return all || !view.live ? list : list.slice(0, WINDOW)
  }, [digest.newestFirst, focus, all, view.live])

  const caption = captionFor(view, digest.reported)
  const focused = focus === null ? undefined : view.workers.find((w) => w?.worker === focus)

  /*
   * Held once per tab, at the top level.
   *
   * Hooks in JSX props would be legal — the call order is stable — but it reads
   * as a rules-of-hooks violation to every future reader and to most linters.
   * Named locals also make the optimisation legible: three of these four pairs
   * are frozen at any moment, and that is the whole performance story.
   */
  const overviewHunt = useHeld(view, tab === 'overview')
  const overviewDigest = useHeld(digest, tab === 'overview')
  const workersHunt = useHeld(view, tab === 'workers')
  const workersDigest = useHeld(digest, tab === 'workers')
  const requestRows = useHeld(rows, tab === 'requests')
  const requestsDigest = useHeld(digest, tab === 'requests')
  const errorsHunt = useHeld(view, tab === 'errors')
  const errorsDigest = useHeld(digest, tab === 'errors')
  const accountsHunt = useHeld(view, tab === 'accounts')
  const logHunt = useHeld(view, tab === 'log')
  const searchHunt = useHeld(view, tab === 'search')
  const gaHunt = useHeld(view, tab === 'ga')
  const activeHunt = useHeld(view, tab === 'active')

  /*
   * The biggest ring anyone got, as the yardstick for "short".
   *
   * The configured target is a server setting the panel does not have, and
   * comparing against a constant would be wrong the moment it is changed. What
   * matters is the IMBALANCE anyway: a worker holding one identity while its
   * siblings hold five is throttled for a reason nothing else on screen shows,
   * and that comparison needs no configuration to make.
   */
  const maxRing = React.useMemo(
    () => view.workers.reduce((n, w) => (w == null ? n : Math.max(n, w.accountsTotal)), 0),
    [view.workers]
  )

  return (
    /*
      Fills its column, rather than a fixed 26rem.

      The height used to be pinned here and the run console's derived from it
      with a `calc` — two magic numbers kept in step by hand, which put five
      worker rows in a letterbox and went negative on a short viewport. The
      drawer's grid owns the height now; this just takes all of it.
    */
    /*
      One integer, supplied once for the whole panel.

      Every tab labels workers and none of them carries the fleet boundary — a
      poll record is a worker NUMBER and nothing more. Threading it would touch
      eight components to hand each the same unchanged value, so it is a context
      taken at the root instead. See `useWorkerTag`.
    */
    <WatchWorkersProvider
      watchWorkers={view.watchWorkers}
      gqlWorkers={view.gql?.workers}
    >
    <section className="flex h-full min-h-0 flex-col overflow-hidden rounded-md border border-line bg-s1">
      {/* -------- header -------- */}
      <header className="flex flex-none flex-wrap items-center gap-x-2 gap-y-1 border-b border-line px-3 py-2">
        <Radar
          aria-hidden="true"
          className={cn('size-3.5 shrink-0', view.live ? 'text-accent' : 'text-faint')}
        />
        <span className="text-[12.5px] text-fg">Hunt</span>
        <span
          className={cn(
            'rounded-xs px-1.5 text-[11px]',
            view.state === 'won'
              ? 'bg-ok-soft text-ok'
              : view.state === 'striking' || view.state === 'bursting'
                ? 'bg-warn-soft text-warn'
                : view.live
                  ? 'bg-s3 text-muted'
                  : 'bg-s3 text-faint'
          )}
        >
          {view.state}
        </span>
        {/*
          Held is not a state, it is a modifier on one — the hunt is still
          `hunting` and still holds everything. Shown beside the state rather
          than replacing it so both facts are on screen at once.
        */}
        {view.live && view.paused ? (
          <span
            className="rounded-xs bg-warn-soft px-1.5 text-[11px] text-warn"
            title={view.pauseReason ?? 'renewing the session'}
          >
            held
          </span>
        ) : null}
        <span className="tabular truncate text-[11px] text-faint">
          sched {view.scheduleId}
        </span>
        {/*
          The seat as the schedule workers last read it — one line, because it
          is the figure the give-up rule runs on and nothing else on the panel
          carries it. Null on the Brazil flow, absent on a retained view; both
          render nothing rather than a dash that would claim a read was made.
        */}
        {view.seats == null ? null : <SeatLine seats={view.seats} />}

        <span className="tabular ml-auto flex items-center gap-2 text-[11.5px] text-faint">
          <span>
            {duration(view.live || view.endedAt === null ? view.elapsedMs : view.endedAt - view.startedAt)}
            {view.live ? ` · ${view.rate}/s` : ''}
          </span>
          {/*
            Removed when stopped, not disabled.
            A freeze toggle on something that cannot move is a control that
            appears to do nothing, which is worse than its absence.
          */}
          {view.live ? (
            <button
              type="button"
              onClick={() => setFrozen((f) => !f)}
              title={frozen ? 'Resume live updates' : 'Freeze the view so it can be read'}
              className={cn(
                'flex items-center gap-1 rounded-xs px-1.5 py-0.5 text-[11px]',
                frozen ? 'bg-warn-soft text-warn' : 'text-faint hover:bg-s2 hover:text-muted'
              )}
            >
              {frozen ? (
                <Play aria-hidden="true" className="size-3" />
              ) : (
                <Pause aria-hidden="true" className="size-3" />
              )}
              {frozen ? 'frozen' : 'live'}
            </button>
          ) : (
            <span className="text-faint">
              frozen{view.endedAt === null ? '' : ` at ${stamp(view.endedAt)}`} · not polling
            </span>
          )}
        </span>
      </header>

      {/* -------- stages -------- */}
      <div className="flex-none border-b border-line">
        <HuntStages hunt={view} reported={digest.reported} caption={caption} />
      </div>

      {/* -------- tab strip -------- */}
      <TabStrip
        value={tab}
        onChange={setTab}
        counts={{
          overview: null,
          /*
           * Attempts MADE, not attempts held.
           *
           * `strikes` is a page of an uncapped stream — what this console has
           * accumulated since the drawer opened — so counting it would put a
           * number on the tab that starts low and climbs as the panel catches
           * up. `strikeTotal` is the run's own count and is what the badge meant
           * all along; the fallback keeps a pre-`strikeTotal` view readable.
           */
          claim: (view.strikeTotal ?? view.strikes.length) || null,
          active: view.actives === undefined || view.actives === 0 ? null : view.actives,
          workers: view.workers.length,
          log: focus === null ? null : (view.workerLogCounts?.[focus] ?? 0),
          /*
           * SEARCHES, not matches, and not the focused worker's ring.
           *
           * The ring is a few seconds deep and only populated once somebody has
           * clicked a worker, so counting it would show a badge that reads zero
           * for a detector making thousands of calls a second and then jumps
           * when a worker is picked. The fleet's whole-run total is the figure
           * that means something at a glance, and it is already on the payload.
           */
          search: view.gql == null ? null : view.gql.polls,
          // And the GA fleet, on the same rule. This one runs for hours, so its
          // total is the largest number on the strip and reading it climb is how
          // an operator sees the fleet is alive at all.
          ga: view.ga == null ? null : view.ga.polls,
          accounts: view.accounts_detail?.length ?? null,
          requests: rows.length,
          errors: view.errors,
        }}
        warn={{
          overview: false,
          // Only when the claim was ATTEMPTED AND LOST. A won hunt must not wear
          // a warning, and a hunt that has not struck yet has nothing to say.
          claim: (view.strikeTotal ?? view.strikes.length) > 0 && view.state !== 'won',
          /*
           * Warns only on the outcome worth acting on: the shift OPENED and we
           * did not get it. A count on its own is neutral — an opening seen and
           * won is the system working — so the badge is tied to the claim, not
           * to the sighting.
           */
          active: (view.actives ?? 0) > 0 && view.claim == null,
          log: false,
          /*
           * The three ways this detector fails while every counter reads healthy.
           *
           * A run of searches that has never carried a single card means the job
           * id, the filters or the query are wrong — 200 OK forever, nothing to
           * match. `drift` means it is now writing ids in a shape the match
           * cannot read, so it has stopped being able to see its own target.
           * `stuck` means rotations are being asked for and declined, leaving
           * workers on addresses they have already given up on.
           *
           * Refusals deliberately do NOT warn here. This fleet expects them —
           * that is what rotation is for — and a badge that is always lit is a
           * badge nobody reads. The `refused` tile carries the ratio.
           */
          search:
            view.gql != null &&
            (view.gql.drift ||
              view.gql.stuck > 0 ||
              (view.gql.polls > 0 && view.gql.cardsMax === 0)),
          /*
           * The two ways THIS fleet fails while every counter reads healthy.
           *
           * `lastTotal === null` after real reads is the important one: 200s that
           * carry no `availableSchedules.total` are a bot check, and they read as
           * "this job is advertising nothing" everywhere else on the screen —
           * including in the customer's own drawer, which has nothing else to
           * say. A customer can wait on that forever.
           *
           * `stuck` is the other: rotations asked for and declined, leaving
           * workers on addresses they have already given up on.
           *
           * Refusals deliberately do NOT warn, on the same rule as the search
           * fleet above.
           */
          ga: view.ga != null && (view.ga.stuck > 0 || (view.ga.polls > 0 && view.ga.lastTotal === null)),
          workers: digest.verdicts.length > 0,
          // A ring below its target is the finding this tab was built for, and
          // it is invisible everywhere else until the 429s start.
          accounts: view.workers.some((w) => w != null && w.accountsTotal > 0 && w.accountsTotal < maxRing),
          requests: false,
          errors: view.errors > 0,
        }}
      />

      {/* -------- focus bar, on every tab, so the filter is never invisible -------- */}
      {focused === undefined ? null : (
        <div className="tabular flex flex-none items-center gap-2 border-b border-line/60 bg-accent-soft px-3 py-1 text-[11px]">
          <span className="text-fg">{roleTag(focused)}</span>
          <span className="truncate text-faint">{focused.egress}</span>
          <button
            type="button"
            onClick={clearFocus}
            aria-label="Clear worker focus"
            className="ml-auto flex items-center gap-1 rounded-xs px-1 text-accent hover:bg-s2"
          >
            <X aria-hidden="true" className="size-3" />
            clear
          </button>
        </div>
      )}

      {/* -------- panels: hidden, never unmounted -------- */}
      <div className="min-h-0 flex-1">
        <Panel active={tab === 'claim'} id="claim">
          <ClaimTab
            strikes={view.strikes}
            total={view.strikeTotal}
            degraded={view.strikesDegraded}
            state={view.state}
            claim={view.claim}
          />
        </Panel>
        <Panel active={tab === 'active'} id="active">
          <ActiveHitsTab hunt={activeHunt} onFocusWorker={onFocus} />
        </Panel>

        <Panel active={tab === 'overview'} id="overview">
          <OverviewTab
            hunt={overviewHunt}
            digest={overviewDigest}
            focus={focus}
            onFocus={onFocus}
            onOpenErrors={openErrors}
          />
        </Panel>
        <Panel active={tab === 'workers'} id="workers">
          <WorkersTab
            hunt={workersHunt}
            digest={workersDigest}
            focus={focus}
            onFocus={onFocus}
          />
        </Panel>
        <Panel active={tab === 'requests'} id="requests">
          <RequestsTab
            rows={requestRows}
            digest={requestsDigest}
            focus={focus}
            all={all || !view.live}
            onToggleAll={toggleAll}
            onClearFocus={clearFocus}
            onFocusWorker={onFocus}
            url={view.url}
            trace={traceControls}
          />
        </Panel>
        <Panel active={tab === 'log'} id="log">
          <WorkerLogTab hunt={logHunt} worker={focus} onSelectWorker={setFocus} />
        </Panel>
        <Panel active={tab === 'search'} id="search">
          <SearchTab hunt={searchHunt} worker={focus} onFocusWorker={onFocus} />
        </Panel>
        <Panel active={tab === 'ga'} id="ga">
          <GaTab hunt={gaHunt} worker={focus} onFocusWorker={onFocus} />
        </Panel>
        <Panel active={tab === 'accounts'} id="accounts">
          <AccountsTab hunt={accountsHunt} onFocusWorker={onFocus} />
        </Panel>
        <Panel active={tab === 'errors'} id="errors">
          <ErrorsTab
            hunt={errorsHunt}
            digest={errorsDigest}
            focus={focus}
            onFocusWorker={onFocus}
            onClearFocus={clearFocus}
          />
        </Panel>
      </div>
    </section>
    </WatchWorkersProvider>
  )
})

/* ------------------------------------------------------------------ *
 * The hidden-tab optimisation
 * ------------------------------------------------------------------ */

/**
 * A value that only moves while its tab is on screen.
 *
 * Panels stay mounted so they keep their scroll position and their expanded
 * bodies — which would otherwise mean four tabs re-deriving and re-rendering
 * 1.4 times a second to paint pixels nobody can see. Holding the reference
 * still while a tab is hidden makes that tab's `React.memo` bail on a single
 * `Object.is`, so three of the four panels cost nothing per poll.
 *
 * Switching back hands over the CURRENT frame rather than the one you left,
 * which is the right behaviour: a tab should show now, not a moment in the
 * past. Assigning a ref during render is legal here because it is a cache — the
 * value is derived from the props, never from anything outside React.
 */
function useHeld<T>(value: T, active: boolean): T {
  const held = React.useRef(value)
  if (active) held.current = value
  return held.current
}

function Panel({
  active,
  id,
  children,
}: {
  active: boolean
  id: string
  children: React.ReactNode
}) {
  return (
    <div
      role="tabpanel"
      id={`hunt-panel-${id}`}
      aria-labelledby={`hunt-tab-${id}`}
      hidden={!active}
      className={active ? 'h-full min-h-0' : 'hidden'}
    >
      {children}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Tab strip
 *
 * Hand-rolled to match `session-drawer.tsx` — a roving tabindex and four ARIA
 * attributes, which is not worth a dependency.
 * ------------------------------------------------------------------ */

/*
 * Claim sits SECOND, immediately after Overview.
 *
 * Tab order is a claim about importance, and this one call is what the whole
 * system exists to make: whether a shift was won is decided by update-application
 * and by nothing else. Everything to its right — workers, requests, errors — is
 * instrumentation about the watching that leads up to it.
 */
const TABS: readonly { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'claim', label: 'Claim' },
  /*
   * Then Active, because it is the other half of the same story.
   *
   * Claim is what we DID about an opening; this is the opening itself — every
   * worker that saw it, as which identity, how far apart. A run that saw three
   * and won none and a run that never saw one are indistinguishable everywhere
   * else in this panel, and they call for opposite responses.
   */
  { key: 'active', label: 'Active' },
  { key: 'workers', label: 'Workers' },
  // Directly after Workers: picking one there is the question, and this is the
  // answer — every call it has made, in order.
  { key: 'log', label: 'Log' },
  /*
   * Then Search, because it is the Log's other half.
   *
   * The Log carries every call a worker made as timings and statuses, which is
   * the whole story for a schedule watcher: a 200 there means the shift's status
   * was read. It is not the whole story for a shift-list worker, whose failure
   * mode is a 200 carrying an empty list — invisible in a status code, and
   * identical to a job that is simply advertising nothing. This is where the
   * request and the response bodies that separate those two live.
   */
  { key: 'search', label: 'Search' },
  /*
   * Then GA, the fleet that reads a shift's UUID off the job's listing.
   *
   * On a Current run it arms beside the watch only while the assigned shift has
   * no UUID, together with the shift-list workers, and stands down once one of
   * them finds it — so on most runs this tab is empty, and that is correct
   * rather than wasteful: an empty tab says the shift already has its name.
   * It used to run BEFORE there was anything to claim, waiting for a job to
   * advertise a shift so an application could be created; the create now goes
   * out at Start and nothing runs before an application exists.
   *
   * Its failure mode is the worst of the three: a 200 carrying a body with no
   * total in it, which reads as "this job is advertising nothing" and is
   * indistinguishable from the truth for as long as the fleet runs.
   */
  { key: 'ga', label: 'GA' },
  // Then Accounts, because it answers the question the Log raises: a worker has
  // an address AND a ring of identities, and only this tab says which of the two
  // a refusal is actually about.
  { key: 'accounts', label: 'Accounts' },
  { key: 'requests', label: 'Requests' },
  { key: 'errors', label: 'Errors' },
]

const TabStrip = React.memo(function TabStrip({
  value,
  onChange,
  counts,
  warn,
}: {
  value: TabKey
  onChange: (key: TabKey) => void
  counts: Record<TabKey, number | null>
  warn: Record<TabKey, boolean>
}) {
  const refs = React.useRef<Array<HTMLButtonElement | null>>([])

  /*
   * Keep the selected tab on screen.
   *
   * The strip scrolls, and the tab that is selected is not always the tab that
   * was clicked: arrow keys move the selection, and reopening a drawer restores
   * whichever tab was last read — which on a narrow panel can be one of the ones
   * off the right edge. Without this it would simply appear that nothing is
   * selected.
   *
   * `nearest` on both axes so a tab already in view is left alone and no
   * ancestor is scrolled: the drawer this sits in is itself scrollable, and
   * `block: 'center'` here would drag the whole console around to line up a
   * 32px strip.
   */
  React.useEffect(() => {
    const index = TABS.findIndex((t) => t.key === value)
    refs.current[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [value])

  const move = (from: number, delta: number) => {
    const next = (from + delta + TABS.length) % TABS.length
    const tab = TABS[next]
    if (!tab) return
    refs.current[next]?.focus()
    onChange(tab.key)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault()
        move(index, 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault()
        move(index, -1)
        break
      case 'Home':
        e.preventDefault()
        move(0, 0)
        break
      case 'End':
        e.preventDefault()
        move(0, TABS.length - 1)
        break
      default:
        break
    }
  }

  return (
    /*
      One row, scrolled sideways — never wrapped.

      Ten tabs do not fit a drawer on a laptop, and the two answers to that
      are not equivalent. Wrapping pushes every panel below down by a line, and
      it does so at whatever width the last tab happens to break at, so the
      whole console shifts as counts grow from `95` to `102,348`. Scrolling
      leaves the layout still and moves only the thing that overflowed.

      `shrink-0` on the buttons is what makes it scroll rather than squeeze: a
      flex row's default is to compress its children to fit, which would shave
      the labels down before any overflow existed to scroll.
    */
    <div
      role="tablist"
      aria-label="Hunt view"
      aria-orientation="horizontal"
      className="rail-x flex flex-none items-center gap-1 border-b border-line px-2"
    >
      {TABS.map((tab, index) => {
        const active = tab.key === value
        const n = counts[tab.key]
        return (
          <button
            key={tab.key}
            ref={(el) => {
              refs.current[index] = el
            }}
            id={`hunt-tab-${tab.key}`}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`hunt-panel-${tab.key}`}
            tabIndex={active ? 0 : -1}
            onKeyDown={(e) => onKeyDown(e, index)}
            onClick={() => onChange(tab.key)}
            className={cn(
              'relative flex h-8 shrink-0 items-center gap-1.5 px-2.5 text-[12px] whitespace-nowrap transition-colors duration-100',
              active ? 'font-medium text-fg' : 'text-faint hover:text-muted'
            )}
          >
            <span>{tab.label}</span>
            {n === null ? null : (
              <span className={cn('tabular text-[11px]', warn[tab.key] ? 'text-warn' : 'text-faint')}>
                {count(n)}
              </span>
            )}
            {warn[tab.key] ? <span className="text-warn">⚠</span> : null}
            {/*
              `bottom-0`, not `-bottom-px`.

              A scroll container clips its overflow in BOTH axes — `overflow-x`
              cannot be auto while `overflow-y` stays visible — so an underline
              sitting one pixel below the button would be cut off the moment the
              strip started scrolling. Inside the box it lands directly on top of
              the strip's own bottom border, which is where it looked anyway.
            */}
            {active ? (
              <span
                aria-hidden="true"
                className="absolute inset-x-1 bottom-0 h-[2px] rounded-full bg-accent"
              />
            ) : null}
          </button>
        )
      })}
    </div>
  )
})

/* ------------------------------------------------------------------ *
 * The seat, as the schedule workers last read it
 * ------------------------------------------------------------------ */

/** `3s ago`, `2m ago` — coarse on purpose; reads are at most seconds apart. */
function ago(ms: number): string {
  if (ms < 1_500) return 'just now'
  if (ms < 90_000) return `${Math.round(ms / 1000)}s ago`
  return `${Math.round(ms / 60_000)}m ago`
}

/**
 * "Seats 3 · read 2s ago · empty 12/30", in the header's own voice.
 *
 * Warns on the two readings that call for a look: a zero, which is the run
 * ticking towards giving the shift up, and a body with no count in it, which is
 * the run unable to judge at all — a schedule that has changed shape looks
 * exactly like one that is quietly full, and only this line can tell them apart.
 * The empty run is shown against its cap when the rule is on, and bare when it
 * is off, so a full shift is still visible under a rule nobody has turned on.
 *
 * An ASSIGNED pair — job and shift set on the customer — is never given up on
 * the count, so its line says so instead of an empty run, and never warns: the
 * figure is information, not a countdown.
 */
function SeatLine({ seats }: { seats: NonNullable<HuntLiveDto['seats']> }) {
  const parts: string[] = []
  if (seats.assigned) {
    parts.push(seats.count === null ? 'Seats —' : `Seats ${seats.count}`)
    if (seats.ageMs !== null) parts.push(`read ${ago(seats.ageMs)}`)
    parts.push('not checked — assigned pair')
  } else {
    if (seats.unparsed) parts.push('Seats — · no count in the schedule body')
    else if (seats.count === null) parts.push('Seats — · no read yet')
    else parts.push(`Seats ${seats.count}`)
    if (seats.ageMs !== null) parts.push(`read ${ago(seats.ageMs)}`)
    if (seats.emptyRunMax > 0) parts.push(`empty ${seats.emptyRun}/${seats.emptyRunMax}`)
    else if (seats.emptyRun > 0) parts.push(`empty ${seats.emptyRun}`)
  }

  const warn = !seats.assigned && (seats.unparsed || seats.count === 0)
  return (
    <span
      className={cn('tabular truncate text-[11px]', warn ? 'text-warn' : 'text-faint')}
      title={
        'laborDemandAvailableCount, as the schedule workers last read it off the schedule body. ' +
        (seats.assigned
          ? 'The job and shift were set on the customer, so the count is shown and never judged: an assigned pair is never given up.'
          : seats.emptyRunMax > 0
            ? `"empty" counts consecutive reads with no free seat against the give-up setting (${seats.emptyRunMax}); any read with a free seat resets it, and a body with no count moves it neither way.`
            : '"empty" counts consecutive reads with no free seat; the give-up rule for it is off.')
      }
    >
      {parts.join(' · ')}
    </span>
  )
}

/* ------------------------------------------------------------------ *
 * The one-line summary under the stage bar
 * ------------------------------------------------------------------ */

function captionFor(hunt: HuntLiveDto, reported: number): string {
  const index = stageIndex(hunt, reported)
  const total = hunt.workers.length

  /*
   * A held hunt says so BEFORE anything else is computed.
   *
   * Every other caption is derived from movement — checks completed, workers
   * reporting, the last status read — and none of those move while the workers
   * are held. Falling through would describe a hunt frozen mid-sentence, which
   * reads as a stall rather than as the deliberate, seconds-long pause it is.
   */
  if (hunt.live && hunt.paused) {
    return `held — ${hunt.pauseReason ?? 'renewing the session'} · every address and identity kept · resuming automatically`
  }

  if (!hunt.live) {
    const reached = ['Arm', 'Spread', 'Watch', 'Strike', 'Claimed'][index] ?? 'Arm'
    if (hunt.state === 'won') {
      return `shift claimed after ${count(hunt.polls)} checks in ${duration(
        hunt.endedAt === null ? hunt.elapsedMs : hunt.endedAt - hunt.startedAt
      )}`
    }
    return `${hunt.endedReason ?? 'stopped'} · reached ${reached} · ${count(
      hunt.polls
    )} checks · never saw ACTIVE`
  }

  switch (index) {
    case 0:
      return 'leasing addresses and claiming identities · no worker has completed a check yet'
    case 1:
      return `${reported} of ${total} workers have completed a first check`
    case 3:
      return 'claiming the shift — update-application in flight'
    case 4:
      return `shift claimed after ${count(hunt.polls)} checks`
    default:
      return `${reported} of ${total} workers reporting · ${hunt.accounts} identities · ${count(
        hunt.polls
      )} checks · last read ${hunt.lastStatus ?? 'unknown'}`
  }
}
